/****************************************************************************
 * Copyright (C) 2014-2017 ecsec GmbH.
 * All rights reserved.
 * Contact: ecsec GmbH (info@ecsec.de)
 *
 * This file is part of SkIDentity.
 *
 * This file may be used in accordance with the terms and conditions
 * contained in a signed written agreement between you and ecsec GmbH.
 *
 ***************************************************************************/
 
//import { CryptoInterface, KeyInterface } from "../interface";
import { Logger, Storage } from "../../util";
import * as util from "../../util/UtilFunctions";
import * as cryptoUtil from "../util";
import { ECKey, ExportedECKey } from "../../pairing/jwk";
import sjcl = require("@ecsec/sjcl");

export function loadKey(id: string, secret: string): KeyInterface {
	let savedKey = Storage.load(id) as SerializedKey;
	let loadedKey = deserializeKeys(savedKey);

	loadedKey.sec.unmask(secret, savedKey.salt);

	let key = new WebCryptoKey(loadedKey, id);
	return key;
}

function deserializeKeys(kSer: SerializedKey): sjcl.SjclKeyPair<sjcl.SjclEcdsaPublicKey,sjcl.SjclEcdsaSecretKey> {
	return {
		sec: sjcl.ecc.deserialize(kSer.priv) as sjcl.SjclEcdsaSecretKey,
		pub: sjcl.ecc.deserialize(kSer.pub) as sjcl.SjclEcdsaPublicKey
	};
}
	
export function saveKey(key: KeyInterface, secret: string): string {
	let k = key.getSjclKey();
	let salt = util.randomString(20);

	let maskSecKey = k.sec.mask(secret, salt);

	let secKey = maskSecKey.serialize();
	let pubKey = k.pub.serialize();

	let toSave: SerializedKey = {
		priv : secKey,
		pub : pubKey,
		salt : salt
	};

	let id = Storage.save(toSave, true);
	return id;
}
	
export function genKey(curve: ec): KeyInterface {
	let k;
	switch(curve) {
		case ec.c256:
			k = sjcl.ecc.ecdsa.generateKeys(sjcl.ecc.curves.c256);
		break;
		case ec.c384:
			k = sjcl.ecc.ecdsa.generateKeys(sjcl.ecc.curves.c384);
		break;
		default:
			k = sjcl.ecc.ecdsa.generateKeys(sjcl.ecc.curves.c256);
	}
	return new WebCryptoKey(k);
}
	
export function hashHex(toHash: any, algo: hashAlgo): string {
	return sjcl.codec.hex.fromBits(hashRaw(toHash, algo));
}

export function hashBase64(toHash: any, algo: hashAlgo): string {
	return sjcl.codec.base64.fromBits(hashRaw(toHash, algo));
}

export function hashBase64url(toHash: any, algo: hashAlgo): string {
	return sjcl.codec.base64url.fromBits(hashRaw(toHash, algo));
}
	
export function hashRaw(toHash: any, algo: hashAlgo): sjcl.BitArray {
	switch (algo) {
		case hashAlgo.sha1: return sjcl.hash.sha1.hash(toHash);
		case hashAlgo.sha256: return sjcl.hash.sha256.hash(toHash);
		case hashAlgo.sha512: return sjcl.hash.sha512.hash(toHash);
		default: return null;
	}
}

export class WebCryptoKey implements KeyInterface {
	private sjclKey: sjcl.SjclKeyPair<sjcl.SjclEcdsaPublicKey, sjcl.SjclEcdsaSecretKey>;
	private keyID: string;
	
	constructor(key: sjcl.SjclKeyPair<sjcl.SjclEcdsaPublicKey, sjcl.SjclEcdsaSecretKey>, kID: string = null) {
		this.sjclKey = key;
		this.keyID = kID;
	}
	
	getSjclKey(): sjcl.SjclKeyPair<sjcl.SjclEcdsaPublicKey, sjcl.SjclEcdsaSecretKey> {
		return this.sjclKey;
	}
	
	getCurve(): ec {
		let c = this.sjclKey.pub.serialize().curve;
		switch(c) {
			case "c256": return ec.c256;
			case "c384": return ec.c384;
		}
	}
	
	getJWSalg(): string {
		switch (this.getCurve()) {
			case ec.c256: return "ES256";
			case ec.c384: return "ES386";
		}
	}
	
	signAndReturnJWS(payload: sjcl.BitArray, hashAlgo: hashAlgo): string {
		let b64Payload = sjcl.codec.base64url.fromBits(payload);
		
		let jwk = new ECKey();
		jwk.loadSjclPubKey(this.sjclKey.pub);
		
		let jws: JWS = {
			alg : this.getJWSalg(),
			kid : this.keyID,
			jwk : jwk.exportJWK()
		};
		
		let str: string = window.btoa(JSON.stringify(jws)).replace(/=+$/, "");
		str += ".";
		str += b64Payload;
		
		let bitArr = hashRaw(str, hashAlgo);
		let signedJWS = sjcl.codec.base64url.fromBits(this.sjclKey.sec.sign(bitArr));
		
		str += ".";
		str += signedJWS;
		
		return str;
	}
}

export interface KeyInterface {	
	getSjclKey(): sjcl.SjclKeyPair<sjcl.SjclEcdsaPublicKey, sjcl.SjclEcdsaSecretKey>;
	getCurve(): ec;
	getJWSalg(): string;
	signAndReturnJWS(payload: any, hashAlgo: string): string;
} 

export enum hashAlgo {
	sha1 = "SHA-1",
	sha256 = "SHA-256",
	sha512 = "SHA-512"
}

export enum ec {
	c256 = "c256",
	c384 = "c384"
}

interface SerializedKey {
	priv: sjcl.SjclECCSecretKeySerialized;
	pub: sjcl.SjclECCPublicKeySerialized;
	salt: string;
}

interface JWS {
	alg: string;
	jwk?: ExportedECKey;
	kid?: string;
}


// WEBPACK FOOTER //
// ./node_modules/@ecsec/base-libs/src/webcrypto/impl/WebCrypto.ts